import { useQueryParams } from '@/hooks';
import { useRequestGetPromotions } from '@/hooks/httpRequest/promotions';
import { DataTablePageSizeEnum, PromotionProductEnum, PromotionStatusEnum } from '@/enums';
import { useCallback, useEffect } from 'react';

const ALL_PRODUCTS = [PromotionProductEnum.BOOST, PromotionProductEnum.COINS, PromotionProductEnum.INCOGNITO];
const ALL_STATUSES = [PromotionStatusEnum.PENDING, PromotionStatusEnum.ACTIVE, PromotionStatusEnum.FINISHED];

const usePromotions = () => {
	const { updateSearchParams, urlSearchParams, values: [page, limit] } = useQueryParams(
		['page', 'limit', 'products', 'status', 'periodFrom', 'periodTo'],
		{ page: 1, limit: DataTablePageSizeEnum._15 },
	);

	useEffect(() => {
		if (!urlSearchParams.has('status') && !urlSearchParams.has('products')) {
			updateSearchParams(prev => {
				ALL_PRODUCTS.forEach(p => prev.append('products', p));
				ALL_STATUSES.forEach(s => prev.append('status', s));
				return prev;
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const products = urlSearchParams.getAll('products') as PromotionProductEnum[];
	const status = urlSearchParams.getAll('status') as PromotionStatusEnum[];
	const periodFrom = urlSearchParams.get('periodFrom') ?? undefined;
	const periodTo = urlSearchParams.get('periodTo') ?? undefined;

	const result = useRequestGetPromotions({
		products: products.length ? products : undefined,
		status: status.length ? status : undefined,
		periodFrom,
		periodTo,
		page,
		limit,
	});

	const handleFilterChange = useCallback((key: string, values: string[]) => {
		updateSearchParams(prev => {
			prev.delete(key);
			values.forEach(v => prev.append(key, v));
			return prev;
		});
	}, [updateSearchParams]);

	const handlePeriodChange = useCallback((from?: string, to?: string) => {
		updateSearchParams(prev => {
			if (from) prev.set('periodFrom', from); else prev.delete('periodFrom');
			if (to) prev.set('periodTo', to); else prev.delete('periodTo');
			return prev;
		});
	}, [updateSearchParams]);

	const clearFilters = useCallback(() => {
		updateSearchParams(prev => {
			prev.delete('products');
			prev.delete('status');
			prev.delete('periodFrom');
			prev.delete('periodTo');
			return prev;
		});
	}, [updateSearchParams]);

	const hasActiveFilters = products.length > 0 || status.length > 0 || !!periodFrom || !!periodTo;

	return {
		...result,
		activeFilters: { products, status, periodFrom, periodTo },
		hasActiveFilters,
		handleFilterChange,
		handlePeriodChange,
		clearFilters,
	};
};

export default usePromotions;
